import { motion } from 'framer-motion';
import { Camera, Car, Edit, MapPin, Package, Plus, Save, Upload, User, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { typography } from '../../styles/typography';
import {
  createInventoryItem,
  getAllEmployees,
  getAllVehicles,
  getSites,
} from '../Inventory/inventoryOnboardingApi.js';
import { Button, Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input.js';
import './InventoryItemOnboarding.css';
import MultiSelectDialog from './MultiSelectDialog.js';

const INVENTORY_TYPES = [
  'Electronics',
  'Furniture',
  'Tools',
  'Office Supplies',
  'Other',
];

const SOURCE_TYPES = [
  { value: 'site', label: 'Site', icon: MapPin },
  { value: 'vehicle', label: 'Vehicle', icon: Car },
  { value: 'employee', label: 'Employee', icon: User },
];

// Custom categories storage key
const CUSTOM_CATEGORIES_KEY = 'inventory_custom_categories';

// Load custom categories from localStorage
const loadCustomCategories = () => {
  try {
    const stored = localStorage.getItem(CUSTOM_CATEGORIES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save custom categories to localStorage
const saveCustomCategories = (categories) => {
  try {
    localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify(categories));
  } catch (error) {
    console.error('Error saving custom categories:', error);
  }
};

const INITIAL_FORM_STATE = {
  itemId: '', // Auto-generated, disabled field
  name: '',
  description: '',
  type: '',
  specifications: '',
  sourceType: '',
  sourceId: '',
  imageData: null,
};

const InventoryItemOnboarding = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasImage, setHasImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const [customCategories, setCustomCategories] = useState(loadCustomCategories());
  const [fieldErrors, setFieldErrors] = useState({});

  // Data loading state
  const [sites, setSites] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Source selection state
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);

  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Cleanup camera on component unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Reset source selections when sourceType changes
  useEffect(() => {
    setSelectedSites([]);
    setSelectedVehicles([]);
    setSelectedEmployees([]);
    setFormData(prev => ({ ...prev, sourceId: '' }));
  }, [formData.sourceType]);

  // Generate Item ID when form is initialized
  useEffect(() => {
    if (!formData.itemId) {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000);
      setFormData(prev => ({ ...prev, itemId: `INV-${timestamp}-${random}` }));
    }
  }, []);

  const fetchInitialData = async () => {
    setIsLoadingData(true);
    try {
      console.log('Fetching sites, vehicles, and employees...');

      const [sitesResponse, vehiclesResponse, employeesResponse] =
        await Promise.all([
          getSites(),
          getAllVehicles(),
          getAllEmployees(),
        ]);

      console.log('Sites response:', sitesResponse);
      console.log('Vehicles response:', vehiclesResponse);
      console.log('Employees response:', employeesResponse);

      setSites(sitesResponse || []);
      setVehicles(vehiclesResponse || []);
      setEmployees(employeesResponse || []);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      setError(
        'Failed to load sites, vehicles, or employees. Please refresh the page.'
      );
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError(
        `Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`
      );
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      // Clear image error when user uploads an image
      if (fieldErrors.image) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.image;
          return newErrors;
        });
      }

      const reader = new FileReader();
      reader.onload = e => {
        const base64 = e.target.result.split(',')[1];
        setImageData(base64);
        setImagePreview(e.target.result);
        setHasImage(true);
        setError('');
      };

      reader.onerror = () => {
        setError('Failed to read image file. Please try again.');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setError('Failed to process image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkCameraAvailability = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          available: false,
          error: 'Camera not supported in this browser',
        };
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        device => device.kind === 'videoinput'
      );

      if (videoDevices.length === 0) {
        return { available: false, error: 'No camera found on this device' };
      }

      return { available: true, devices: videoDevices };
    } catch (err) {
      console.error('Error checking camera availability:', err);
      return {
        available: false,
        error: 'Unable to check camera availability',
      };
    }
  };

  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        throw new Error(cameraCheck.error);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);

      let errorMessage = 'Unable to access camera. Please check permissions.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setCameraError(errorMessage);
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0);

      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const base64 = photoDataUrl.split(',')[1];

      setImageData(base64);
      setImagePreview(photoDataUrl);
      setHasImage(true);
      // Clear image error when user captures a photo
      if (fieldErrors.image) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.image;
          return newErrors;
        });
      }

      stopCamera();
    } catch (err) {
      console.error('Error capturing photo:', err);
      setError('Failed to capture photo. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleCameraCapture = () => {
    if (isCameraActive) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Please fill this field';
    }
    if (!formData.type) {
      newErrors.type = 'Please fill this field';
    }
    if (!formData.specifications.trim()) {
      newErrors.specifications = 'Please fill this field';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Please fill this field';
    }
    if (!formData.sourceType) {
      newErrors.sourceType = 'Please fill this field';
    }
    
    // Validate that at least one source item is selected
    let hasSelectedItems = false;
    switch (formData.sourceType) {
      case 'site':
        hasSelectedItems = selectedSites.length > 0;
        break;
      case 'vehicle':
        hasSelectedItems = selectedVehicles.length > 0;
        break;
      case 'employee':
        hasSelectedItems = selectedEmployees.length > 0;
        break;
    }
    
    if (formData.sourceType && !hasSelectedItems) {
      newErrors.sourceSelection = `Please select at least one ${formData.sourceType} using the Add ${formData.sourceType.charAt(0).toUpperCase() + formData.sourceType.slice(1)} button`;
    }
    
    if (!hasImage) {
      newErrors.image = 'Please upload an image';
    }
    
    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Determine source type and ID based on selection (use first selected item)
      let sourceType = formData.sourceType;
      let sourceId = '';
      
      switch (sourceType) {
        case 'site':
          if (selectedSites.length === 0) {
            throw new Error('Please select at least one site');
          }
          sourceId = selectedSites[0].site_id;
          break;
        case 'vehicle':
          if (selectedVehicles.length === 0) {
            throw new Error('Please select at least one vehicle');
          }
          sourceId = selectedVehicles[0].vehicle_id;
          break;
        case 'employee':
          if (selectedEmployees.length === 0) {
            throw new Error('Please select at least one employee');
          }
          sourceId = selectedEmployees[0].user_id;
          break;
        default:
          throw new Error('Invalid source type selected');
      }

      // Prepare payload matching Flutter structure
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        sourceType: sourceType,
        sourceId: sourceId,
        description: formData.description.trim(),
        specifications: formData.specifications.trim(),
        status: 'good_condition', // Default status
        dateOfOnboarding: new Date().toISOString(),
        originalSourceType: sourceType,
        originalSourceId: sourceId,
        imageData: imageData, // Will be handled by API
      };

      console.log('Submitting inventory item:', payload);

      const result = await createInventoryItem(payload);
      setSuccess('Inventory item onboarded successfully!');
      console.log('Inventory item created:', result);

      // Reset form
      setFormData({ ...INITIAL_FORM_STATE, itemId: `INV-${Date.now()}-${Math.floor(Math.random() * 1000)}` });
      setImageData(null);
      setImagePreview(null);
      setHasImage(false);
      setSelectedSites([]);
      setSelectedVehicles([]);
      setSelectedEmployees([]);
      setIsPreview(false);
    } catch (error) {
      console.error('Error onboarding inventory item:', error);
      setError(
        error.message || 'Failed to onboard inventory item. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = (categoryName) => {
    if (!categoryName || categoryName.trim().length < 2) {
      setError('Category name must be at least 2 characters');
      return;
    }

    const allCategories = [...INVENTORY_TYPES, ...customCategories];
    if (allCategories.some(cat => cat.toLowerCase() === categoryName.toLowerCase())) {
      setError('Category already exists');
      return;
    }

    const updatedCategories = [...customCategories, categoryName];
    setCustomCategories(updatedCategories);
    saveCustomCategories(updatedCategories);
    setShowAddCategoryDialog(false);
    setSuccess(`Category "${categoryName}" added successfully`);
  };

  const handleSourceConfirm = (selectedIds, sourceType) => {
    // Clear source selection error when user selects items
    if (fieldErrors.sourceSelection) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sourceSelection;
        return newErrors;
      });
    }
    
    switch (sourceType) {
      case 'site':
        const selectedSitesData = sites.filter(site => selectedIds.includes(site.site_id));
        setSelectedSites(selectedSitesData);
        if (selectedSitesData.length > 0) {
          setFormData(prev => ({ ...prev, sourceId: selectedSitesData[0].site_id }));
        }
        setShowSiteDialog(false);
        break;
      case 'vehicle':
        const selectedVehiclesData = vehicles.filter(vehicle => selectedIds.includes(vehicle.vehicle_id || vehicle.id));
        setSelectedVehicles(selectedVehiclesData);
        if (selectedVehiclesData.length > 0) {
          setFormData(prev => ({ ...prev, sourceId: selectedVehiclesData[0].vehicle_id || selectedVehiclesData[0].id }));
        }
        setShowVehicleDialog(false);
        break;
      case 'employee':
        const selectedEmployeesData = employees.filter(emp => selectedIds.includes(emp.user_id || emp.employee_id));
        setSelectedEmployees(selectedEmployeesData);
        if (selectedEmployeesData.length > 0) {
          setFormData(prev => ({ ...prev, sourceId: selectedEmployeesData[0].user_id || selectedEmployeesData[0].employee_id }));
        }
        setShowEmployeeDialog(false);
        break;
    }
  };


  // Loading state
  if (isLoadingData) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-6xl mx-auto h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading inventory onboarding data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div className="max-w-6xl mx-auto h-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 pt-4 pb-2"
        >
          <h1
            className={typography.h1}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            Inventory Item Onboarding
          </h1>
          <p className={typography.p} style={{ color: '#6B7280' }}>
            Complete the form below to onboard a new inventory item
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="h-full space-y-8">
          {/* Item Image Section */}
          <Card.Glassmorphism className="p-6">
            <div className="text-center">
              <h2
                className={typography.h2}
                style={{ color: '#000000', marginBottom: '1.5rem' }}
              >
                Item Image
              </h2>

              <div className="flex flex-col items-center gap-4">
                {/* Image Preview */}
                <div className="relative">
                  {imagePreview ? (
                    <div className="w-32 h-32 rounded-lg border-4 border-gray-300 overflow-hidden">
                        <img
                        src={imagePreview}
                        alt="Item preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setImageData(null);
                          setHasImage(false);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-lg border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Upload Buttons */}
                {!isPreview && (
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        style={{ color: '#ffffff' }}
                      >
                        <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                        Upload Image or Take Picture
                      </Button>
                      <Button
                        type="button"
                        onClick={handleCameraCapture}
                        className={`flex items-center gap-2 ${
                          isCameraActive
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                        style={{ color: '#ffffff' }}
                      >
                        <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
                        {isCameraActive ? 'Take Photo' : 'Take Photo'}
                      </Button>
                    </div>
                    {fieldErrors.image && (
                      <p className="text-sm text-red-600">{fieldErrors.image}</p>
                    )}
                  </div>
                )}

                {/* Camera Preview */}
                {showCameraPreview && (
                  <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="text-center mb-3">
                      <h3 className="text-lg font-medium text-gray-800 mb-2">
                        Camera Preview
                      </h3>
                      {cameraError && (
                        <p className="text-red-600 text-sm mb-2">
                          {cameraError}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                        />

                        <div className="flex justify-center gap-3 mt-3">
                          <Button
                            type="button"
                            onClick={takePhoto}
                            disabled={isCapturing}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                          >
                            {isCapturing ? 'Capturing...' : 'Capture Photo'}
                          </Button>

                          <Button
                            type="button"
                            onClick={stopCamera}
                            className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hidden Elements */}
                <canvas
                  ref={canvasRef}
                  className="hidden"
                  width="640"
                  height="480"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            </div>
          </Card.Glassmorphism>

          {/* General Information Section */}
          <Card.Gradient className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Package className="w-6 h-6 text-blue-600" />
              <h2
                className={typography.h2}
                style={{ color: '#000000', marginBottom: '0' }}
              >
                General Information
              </h2>
            </div>

            <div className="space-y-6">
              {/* Item Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
  >
                  Item Name *
                </label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  placeholder="Enter item name"
                  required
                  disabled={isPreview}
                  className={isPreview ? 'bg-gray-100' : fieldErrors.name ? 'border-red-500' : ''}
                />
                {fieldErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
                )}
              </div>

              {/* Category Type */}
              <div>
                <label
                  htmlFor="type"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category Type *
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={e => {
                    const value = e.target.value;
                    if (value === '__ADD_NEW_CATEGORY__') {
                      setShowAddCategoryDialog(true);
                    } else {
                      handleInputChange('type', value);
                    }
                  }}
                  className={`w-full p-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldErrors.type ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                  disabled={isPreview}
                >
                  <option value="">Select Category</option>
                  {[...INVENTORY_TYPES, ...customCategories].map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  <option value="__ADD_NEW_CATEGORY__" className="text-blue-600 font-semibold">
                    + Add new category...
                  </option>
                </select>
                {fieldErrors.type && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.type}</p>
                )}
              </div>

              {/* Specifications */}
              <div>
                <label
                  htmlFor="specifications"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Specifications *
                </label>
                <Input
                  id="specifications"
                  type="text"
                  value={formData.specifications}
                  onChange={e =>
                    handleInputChange('specifications', e.target.value)
                  }
                  placeholder="Enter item specifications"
                  required
                  disabled={isPreview}
                  className={isPreview ? 'bg-gray-100' : fieldErrors.specifications ? 'border-red-500' : ''}
                />
                {fieldErrors.specifications && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.specifications}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Description *
                </label>
                <Input
                  id="description"
                  type="text"
                  value={formData.description}
                  onChange={e =>
                    handleInputChange('description', e.target.value)
                  }
                  placeholder="Enter item description"
                  required
                  disabled={isPreview}
                  className={isPreview ? 'bg-gray-100' : fieldErrors.description ? 'border-red-500' : ''}
                />
                {fieldErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.description}</p>
                )}
              </div>
            </div>
          </Card.Gradient>

          {/* Source Information Section */}
          <Card.Light className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-6 h-6 text-purple-600" />
              <h2 className={typography.h2} style={{ color: '#000000' }}>
                Source
              </h2>
            </div>

            <div className="space-y-6">
              {/* Source Type Dropdown */}
              <div>
                <label
                  htmlFor="sourceType"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Source *
                </label>
                <select
                  id="sourceType"
                  value={formData.sourceType}
                  onChange={e => handleInputChange('sourceType', e.target.value)}
                  className={`w-full p-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldErrors.sourceType ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                  disabled={isPreview}
                >
                  <option value="">Select Source Type</option>
                  {SOURCE_TYPES.map(sourceType => (
                    <option key={sourceType.value} value={sourceType.value}>
                      {sourceType.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.sourceType && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.sourceType}</p>
                )}
              </div>

              {/* Add Site Button */}
              {formData.sourceType === 'site' && !isPreview && (
                <div>
                  <Button
                    type="button"
                    onClick={() => setShowSiteDialog(true)}
                    className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Plus className="w-4 h-4" />
                    select Site
                  </Button>
                  {fieldErrors.sourceSelection && formData.sourceType === 'site' && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.sourceSelection}</p>
                  )}
                </div>
              )}

              {/* Selected Sites Display */}
              {formData.sourceType === 'site' && selectedSites.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700">Selected Sites</h4>
                  {selectedSites.map(site => (
                    <div
                      key={site.site_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{site.site_name || 'Unknown'}</p>
                        <p className="text-sm text-gray-600">ID: {site.site_id || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{site.address || 'Location not specified'}</p>
                      </div>
                      {!isPreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSites(prev => prev.filter(s => s.site_id !== site.site_id));
                            if (selectedSites.length === 1) {
                              setFormData(prev => ({ ...prev, sourceId: '' }));
                            }
                          }}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Vehicle Button */}
              {formData.sourceType === 'vehicle' && !isPreview && (
                <div>
                  <Button
                    type="button"
                    onClick={() => setShowVehicleDialog(true)}
                    className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Plus className="w-4 h-4" />
                    select Vehicle
                  </Button>
                  {fieldErrors.sourceSelection && formData.sourceType === 'vehicle' && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.sourceSelection}</p>
                  )}
                </div>
              )}

              {/* Selected Vehicles Display */}
              {formData.sourceType === 'vehicle' && selectedVehicles.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700">Selected Vehicles</h4>
                  {selectedVehicles.map(vehicle => (
                    <div
                      key={vehicle.vehicle_id || vehicle.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {vehicle.registration_no || vehicle.registration_number || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600">ID: {vehicle.vehicle_id || vehicle.id || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{vehicle.vehicle_type || 'Vehicle'}</p>
                      </div>
                      {!isPreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedVehicles(prev => prev.filter(v => (v.vehicle_id || v.id) !== (vehicle.vehicle_id || vehicle.id)));
                            if (selectedVehicles.length === 1) {
                              setFormData(prev => ({ ...prev, sourceId: '' }));
                            }
                          }}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Employee Button */}
              {formData.sourceType === 'employee' && !isPreview && (
                <div>
                  <Button
                    type="button"
                    onClick={() => setShowEmployeeDialog(true)}
                    className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Plus className="w-4 h-4" />
                    select Employee
                  </Button>
                  {fieldErrors.sourceSelection && formData.sourceType === 'employee' && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.sourceSelection}</p>
                  )}
                </div>
              )}

              {/* Selected Employees Display */}
              {formData.sourceType === 'employee' && selectedEmployees.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-700">Selected Employees</h4>
                  {selectedEmployees.map(employee => (
                    <div
                      key={employee.user_id || employee.employee_id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {employee.user_name || employee.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600">
                          ID: {employee.employee_id || employee.user_id || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500">{employee.spec_role || 'Employee'}</p>
                      </div>
                      {!isPreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedEmployees(prev => prev.filter(e => (e.user_id || e.employee_id) !== (employee.user_id || employee.employee_id)));
                            if (selectedEmployees.length === 1) {
                              setFormData(prev => ({ ...prev, sourceId: '' }));
                            }
                          }}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card.Light>

          {/* Error and Success Messages */}
          {error && (
            <div className="self-stretch p-3 bg-red-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-red-200/0 flex flex-col justify-start items-start gap-2.5">
              <div className="justify-start text-red-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                {error}
              </div>
            </div>
          )}

          {success && (
            <div className="self-stretch p-3 bg-green-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-green-200/0 flex flex-col justify-start items-start gap-2.5">
              <div className="justify-start text-green-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                {success}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!success && (
            <div className="flex justify-center gap-4 mt-8">
              {isPreview ? (
                <>
                  <Button
                    type="button"
                    onClick={() => setIsPreview(false)}
                    className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Edit className="w-5 h-5" />
                    Edit
                  </Button>
                  <PrimaryButton
                    type="submit"
                    disabled={isLoading}
                    className="w-auto px-8"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Processing...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Save className="w-5 h-5" style={{ color: '#ffffff' }} />
                        Submit
                      </div>
                    )}
                  </PrimaryButton>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setFieldErrors({});
                    if (validateForm()) {
                      setIsPreview(true);
                    }
                  }}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  style={{ color: '#ffffff' }}
                >
                  Check Preview
                </Button>
              )}
            </div>
          )}
        </form>

        {/* Multi-Select Dialogs */}
        {showSiteDialog && createPortal(
          <MultiSelectDialog
            isOpen={showSiteDialog}
            onClose={() => setShowSiteDialog(false)}
            onConfirm={(selectedIds) => handleSourceConfirm(selectedIds, 'site')}
            entityType="site"
            dialogTitle="Select Site"
            availableEntities={sites}
            selectedEntityIds={selectedSites.map(s => s.site_id)}
          />,
          document.body
        )}

        {showVehicleDialog && createPortal(
          <MultiSelectDialog
            isOpen={showVehicleDialog}
            onClose={() => setShowVehicleDialog(false)}
            onConfirm={(selectedIds) => handleSourceConfirm(selectedIds, 'vehicle')}
            entityType="vehicle"
            dialogTitle="Select Vehicle"
            availableEntities={vehicles}
            selectedEntityIds={selectedVehicles.map(v => v.vehicle_id || v.id)}
          />,
          document.body
        )}

        {showEmployeeDialog && createPortal(
          <MultiSelectDialog
            isOpen={showEmployeeDialog}
            onClose={() => setShowEmployeeDialog(false)}
            onConfirm={(selectedIds) => handleSourceConfirm(selectedIds, 'employee')}
            entityType="employee"
            dialogTitle="Select Employee"
            availableEntities={employees}
            selectedEntityIds={selectedEmployees.map(e => e.user_id || e.employee_id)}
          />,
          document.body
        )}

        {/* Add Category Dialog */}
        {showAddCategoryDialog && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Add New Category</h3>
                <button
                  onClick={() => setShowAddCategoryDialog(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              <div className="p-4">
                <input
                  type="text"
                  id="newCategoryName"
                  placeholder="Enter category name"
                  className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const categoryName = e.target.value.trim();
                      if (categoryName) {
                        handleAddCategory(categoryName);
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mb-4">
                  This category will be available for future use.
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowAddCategoryDialog(false)}
                    className="bg-gray-300 hover:bg-gray-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('newCategoryName');
                      if (input && input.value.trim()) {
                        handleAddCategory(input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                    style={{ color: '#ffffff' }}
                  >
                    Add Category
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default InventoryItemOnboarding;

